import React, { PropsWithChildren, createContext, useContext, useMemo, useState } from 'react';
import { Dialog, DialogTitle, Divider, Grid } from '@mui/material';
import {
  AutocompleteInput,
  BooleanInput,
  Button,
  CreateBase,
  FormDataConsumer,
  ReferenceInput,
  SaveButton,
  SimpleForm,
  TextInput,
  Toolbar,
  required,
  useDataProvider,
  useNotify,
  useRecordContext,
} from 'react-admin';
import z from 'zod';
import { noop } from 'lodash';

import { DEFAULT_DATA_PACKAGE_NAME } from 'src/utils/defaults/Constants';
import { DataPackageTemplateRecord, ExternalApiClientRecord } from 'src/types';
import CraftTwoColumns from 'src/components/CraftTwoColumns';
import { zodIssuesIntoErrorsMap } from 'src/utils/validation';

interface AddDataPackageDialogContextValue {
  open: boolean;
  setOpen: () => void;
  setClosed: () => void;
  onSuccess?: (() => void) | undefined;
  setOnSuccess: (fn: () => void) => void;
}

const addDataPackageDialogContextDefaultValue = {
  open: false,
  setOpen: noop,
  setClosed: noop,
  setOnSuccess: noop,
};

const AddDataPackageDialogContext = createContext<AddDataPackageDialogContextValue>(
  addDataPackageDialogContextDefaultValue,
);

export const AddDataPackageDialogContextProvider = ({ children }: PropsWithChildren) => {
  const [open, setOpen] = useState(false);
  const [onSuccess, setOnSuccess] = useState<() => void>();

  const value = useMemo(
    () => ({
      open,
      setOpen: () => setOpen(true),
      setClosed: () => setOpen(false),
      onSuccess,
      setOnSuccess: (fn: () => void) => setOnSuccess(() => fn),
    }),
    [open, setOpen],
  );

  return <AddDataPackageDialogContext.Provider value={value}>{children}</AddDataPackageDialogContext.Provider>;
};

export const useAddDataPackageDialogContext = () => useContext(AddDataPackageDialogContext);

const CustomToolbar = ({ onCancel }: { onCancel: () => void }) => (
  <Toolbar>
    <Grid container justifyContent="space-between">
      <Button label="Cancel" onClick={onCancel} />
      <SaveButton />
    </Grid>
  </Toolbar>
);

const customerDataPackageSchema = z.object({
  name: z.string().trim().nonempty(),
  dataPackageTemplateID: z.number().int(),
  enableAllCompanies: z.boolean(),
  companyIds: z
    .string()
    .nonempty()
    .trim()
    .regex(/^[\d\n]+$/, 'Company IDs should only contain IDs, each on a separate line')
    .transform((val) => val.split(/\n+/))
    .optional(),
});

export const AddDataPackageDialog = () => {
  const dialogCtx = useAddDataPackageDialogContext();
  const dataProvider = useDataProvider();
  const record = useRecordContext<ExternalApiClientRecord>();
  const notify = useNotify();

  const handleSubmit = async (rawValues: Record<string, unknown>) => {
    if (!record) return undefined;

    const validationResult = customerDataPackageSchema.safeParse(rawValues);
    if (!validationResult.success) return zodIssuesIntoErrorsMap(validationResult.error.issues);
    const values = validationResult.data;

    try {
      const { data: dataPackageTemplate } = await dataProvider.getOne<DataPackageTemplateRecord>(
        'data_package_templates',
        { id: values.dataPackageTemplateID },
      );

      const customerDataPackageParams = {
        external_api_client_id: record.id,
        data_package_template_id: values.dataPackageTemplateID,
        name: values.name,
        api_fields: {
          data: dataPackageTemplate.api_fields_xrefs.map((xref) => ({
            api_field_id: xref.api_field_id,
          })),
        },
        subscriptions: {
          data: values.enableAllCompanies
            ? [
                {
                  external_api_client_id: record.id,
                  all_companies: true,
                },
              ]
            : values.companyIds?.map((companyID) => ({
                external_api_client_id: record.id,
                company_id: parseInt(companyID, 10),
              })),
        },
      };

      await dataProvider.create('customer_data_packages', {
        data: customerDataPackageParams,
      });
    } catch (e) {
      notify('There was a problem adding the data package. Please try again.', { type: 'error' });
      console.error(e);
      return undefined;
    }

    notify('Data package added successfully.', { type: 'success' });
    if (dialogCtx.onSuccess) dialogCtx.onSuccess();
    dialogCtx.setClosed();
    return undefined;
  };

  return (
    <Dialog open={dialogCtx.open} onClose={dialogCtx.setClosed}>
      <DialogTitle>Add customer data package</DialogTitle>
      <Divider />

      <CreateBase>
        <SimpleForm onSubmit={handleSubmit} toolbar={<CustomToolbar onCancel={dialogCtx.setClosed} />} mt={2} mx={1}>
          <FormDataConsumer>
            {({ formData }) => (
              <CraftTwoColumns
                left={
                  <>
                    <TextInput label="Name" source="name" validate={required()} autoFocus fullWidth />
                    <BooleanInput label="All Companies" source="enableAllCompanies" />
                  </>
                }
                right={
                  <>
                    <ReferenceInput
                      source="dataPackageTemplateID"
                      reference="data_package_templates"
                      sort={{ field: 'name', order: 'ASC' }}
                      filter={{
                        'name@_neq': DEFAULT_DATA_PACKAGE_NAME, // default data package template should be hidden
                        'deleted_at@_is_null': true,
                      }}
                    >
                      <AutocompleteInput
                        label="Template"
                        optionText="name"
                        filterToQuery={(q) => ({ name: q })}
                        validate={required()}
                        fullWidth
                      />
                    </ReferenceInput>
                    <TextInput
                      label="Company ID(s)"
                      source="companyIds"
                      multiline
                      validate={formData.enableAllCompanies ? [] : required()}
                      disabled={formData.enableAllCompanies}
                      fullWidth
                      minRows={15}
                      maxRows={15}
                    />
                  </>
                }
              />
            )}
          </FormDataConsumer>
        </SimpleForm>
      </CreateBase>
    </Dialog>
  );
};
